import {
  PARTNER_MAINTENANCE_INTERVENTION_MODES,
  type PartnerMaintenanceInterventionMode,
  type PartnerMaintenanceScopeInterventionMode,
} from '@evimo/types/enums/partner-maintenance-rules';

export type EffectiveScopeInterventionMode =
  PartnerMaintenanceScopeInterventionMode | null;

export function normalizeHeaderInterventionMode(
  headerMode: PartnerMaintenanceInterventionMode | null | undefined,
): PartnerMaintenanceInterventionMode {
  if (
    headerMode != null &&
    (PARTNER_MAINTENANCE_INTERVENTION_MODES as readonly string[]).includes(
      headerMode,
    )
  ) {
    return headerMode;
  }
  return 'BOTH';
}

/**
 * Effective mode used for exclusivity when a scope row leaves interventionMode null.
 * - BOTH header + null scope → null (covers onsite and remote).
 * - ONSITE/REMOTE header + null scope → that single mode.
 */
export function resolveEffectiveScopeInterventionMode(
  scopeMode: PartnerMaintenanceScopeInterventionMode | null | undefined,
  headerMode: PartnerMaintenanceInterventionMode | null | undefined,
): EffectiveScopeInterventionMode {
  if (scopeMode != null) {
    return scopeMode;
  }
  const header = normalizeHeaderInterventionMode(headerMode);
  if (header === 'ONSITE' || header === 'REMOTE') {
    return header;
  }
  return null;
}

/** Two scope claims overlap when they could apply to the same intervention channel. */
export function scopeInterventionModesOverlap(
  left: EffectiveScopeInterventionMode,
  right: EffectiveScopeInterventionMode,
): boolean {
  if (left == null || right == null) {
    return true;
  }
  return left === right;
}

/** SQL expression: effective intervention mode for a published scope row. */
export const SQL_EFFECTIVE_SCOPE_INTERVENTION_MODE = `COALESCE(
  scope."interventionMode"::text,
  CASE rules."interventionMode"::text
    WHEN 'ONSITE' THEN 'ONSITE'
    WHEN 'REMOTE' THEN 'REMOTE'
    ELSE NULL
  END
)`;

/** SQL predicate: incoming mode column overlaps persisted scope effective mode. */
export const SQL_SCOPE_MODE_OVERLAP_PREDICATE = `(
  inc.mode IS NULL
  OR (${SQL_EFFECTIVE_SCOPE_INTERVENTION_MODE}) IS NULL
  OR inc.mode = (${SQL_EFFECTIVE_SCOPE_INTERVENTION_MODE})
)`;
